/**
 * SelectField — A drop-in replacement for SearchableSelect that renders the
 * shadcn Select component under the hood. Accepts dynamic options arrays with
 * labelKey/valueKey like SearchableSelect but gives the unified shadcn styling.
 */
import { X } from "lucide-react";
import React, { useMemo } from "react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";

import { cn } from "@/lib/utils";

export interface SelectOption {
  [key: string]: any;
}

interface SelectFieldProps<T extends SelectOption> {
  /** The list of options to display */
  options: T[] | Record<string, string> | null | undefined;
  /** Key used to display the option label (supports dot-notation) */
  labelKey?: string;
  /** Key used as the unique value identifier */
  valueKey?: string;
  /** Currently selected value (controlled) – matches against valueKey */
  value?: string | number | null;
  /** Callback when an option is selected */
  onValueChange: (option: T | undefined) => void;
  /** Enable the search/filter input inside the dropdown */
  searchable?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  className?: string;
  contentClassName?: string;
  /** Custom renderer for each option row */
  renderOption?: (option: T) => React.ReactNode;
  /** Custom renderer for the selected value in the trigger button */
  renderValue?: (option: T) => React.ReactNode;
  /** Show a small "×" button to clear the current selection back to empty */
  clearable?: boolean;
  /**
   * Text to display in the trigger when `value` is set but the matching option
   * is not present in `options` (e.g. a legacy/filtered-out value).
   */
  fallbackLabel?: string;
}

function getNestedValue(obj: any, path: string): any {
  return path.split(".").reduce((acc, key) => acc?.[key], obj);
}

export function SelectField<T extends SelectOption>({
  options,
  labelKey = "name",
  valueKey = "id",
  value,
  onValueChange,
  searchable = false,
  placeholder = "Select…",
  disabled = false,
  className,
  contentClassName,
  renderOption,
  renderValue,
  clearable = false,
  fallbackLabel,
}: SelectFieldProps<T>) {
  // Normalize options into uniform array
  const normalizedOptions = useMemo(() => {
    if (!options) return [];

    if (Array.isArray(options)) {
      if (options.length === 0) return [];
      if (typeof options[0] === "string" || typeof options[0] === "number") {
        return (options as unknown as (string | number)[]).map((val) => ({
          item: { [labelKey]: String(val), [valueKey]: val } as unknown as T,
          label: String(val),
          val: String(val),
        }));
      }
      return options.map((item) => ({
        item,
        label: String(getNestedValue(item, labelKey) ?? ""),
        val: String(getNestedValue(item, valueKey) ?? ""),
      }));
    }

    // Record<string, string>
    return Object.entries(options).map(([label, val]) => ({
      item: { [labelKey]: label, [valueKey]: val } as unknown as T,
      label,
      val: String(val),
    }));
  }, [options, labelKey, valueKey]);

  const handleChange = (selectedValue: string) => {
    const found = normalizedOptions.find((opt) => opt.val === selectedValue);
    onValueChange(found?.item);
  };

  // Keep the Select permanently controlled: Radix switches from controlled to
  // uncontrolled (and stops reflecting prop updates) if `value` ever goes from
  // a string to `undefined`. Use "" as the "no selection" sentinel instead —
  // Radix shows the placeholder for "" and no SelectItem is ever given "" as
  // its value (see the `.filter((opt) => opt.val !== "")` below).
  const currentVal = value != null && String(value) !== "" ? String(value) : "";

  // Find selected option for custom renderValue
  const selectedOption = useMemo(() => {
    if (!currentVal) return undefined;
    return normalizedOptions.find((opt) => opt.val === currentVal);
  }, [currentVal, normalizedOptions]);
  return (
    <Select value={currentVal} onValueChange={handleChange} disabled={disabled}>
      <SelectTrigger
        className={cn("w-full", className)}
        endContent={
          clearable && currentVal && !disabled ? (
            <span
              role="button"
              tabIndex={-1}
              aria-label="Clear selection"
              // Perform the clear on pointer-down itself and stop propagation so
              // Radix's trigger never opens the dropdown. We must NOT rely on a
              // follow-up click handler: calling preventDefault on pointerdown
              // suppresses the subsequent click event, so the clear would never
              // fire if it lived in onClick.
              onPointerDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onValueChange(undefined);
              }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              className="-m-0.5 rounded-sm p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </span>
          ) : undefined
        }
      >
        {renderValue && selectedOption ? (
          <span className="truncate">{renderValue(selectedOption.item)}</span>
        ) : selectedOption ? (
          <span className="truncate">{selectedOption.label}</span>
        ) : currentVal && fallbackLabel ? (
          <span className="truncate">{fallbackLabel}</span>
        ) : (
          <SelectValue placeholder={placeholder} />
        )}
      </SelectTrigger>
      <SelectContent searchable={searchable} className={contentClassName}>
        {normalizedOptions
          .filter((opt) => opt.val !== "")
          .map((opt) => (
            <SelectItem key={opt.val} value={opt.val} textValue={opt.label}>
              {renderOption ? renderOption(opt.item) : opt.label}
            </SelectItem>
          ))}
      </SelectContent>
    </Select>
  );
}
